import axios from 'axios';
import { CartItem } from '../types';

// Tipos para MercadoPago
export interface MercadoPagoPreference {
  id: string;
  init_point: string;
  sandbox_init_point: string;
}

export interface MercadoPagoItem {
  id: string;
  title: string;
  quantity: number;
  unit_price: number;
  currency_id: string;
  description: string;
  picture_url?: string;
}

export interface MercadoPagoPreferenceRequest {
  items: MercadoPagoItem[];
  payer: {
    name: string;
    email: string;
  };
  back_urls: {
    success: string;
    failure: string;
    pending: string;
  };
  auto_return: string;
  external_reference: string;
  notification_url?: string;
  expires: boolean;
  expiration_date_to: string;
  statement_descriptor?: string;
}

// Configuración de MercadoPago
const MERCADOPAGO_CONFIG = {
  // En producción, usar variables de entorno
  PUBLIC_KEY: 'APP_USR-b95e4da7-086a-4286-a763-0402c6c6fc9b', // Reemplazar con tu clave pública
  ACCESS_TOKEN: 'APP_USR-4294166206525576-081420-76554eb38aa68ce99c9fc82cfae58230-1301498468', // Reemplazar con tu token de acceso
  BASE_URL: 'https://api.mercadopago.com',
};

// Función para crear preferencia de pago
export const createPaymentPreference = async (
  items: CartItem[],
  customerInfo: { name: string; email: string }
): Promise<MercadoPagoPreference> => {
  try {
    // Convertir items del carrito al formato de MercadoPago
    const mpItems: MercadoPagoItem[] = items.map((item) => ({
      id: item.course.id,
      title: item.course.title,
      quantity: item.quantity,
      unit_price: item.course.price,
      currency_id: 'MXN', // O 'ARS' para pesos argentinos
      description: item.course.shortDescription,
      picture_url: item.course.image,
    }));

    // Obtener la URL base - usar una URL pública para desarrollo
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? window.location.origin 
      : 'https://028c-2806-230-2038-c397-99e1-aeb0-74fe-c29c.ngrok-free.app'; // URL temporal para pruebas
    
    // Crear preferencia de pago
    const preferenceData: MercadoPagoPreferenceRequest = {
      items: mpItems,
      payer: {
        name: customerInfo.name,
        email: customerInfo.email,
      },
      back_urls: {
        success: `${baseUrl}/payment/success`,
        failure: `${baseUrl}/payment/failure`,
        pending: `${baseUrl}/payment/pending`,
      },
      auto_return: 'approved',
      external_reference: `order_${Date.now()}`, // Referencia única para el pedido
      expires: true,
      expiration_date_to: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 minutos
    };

    console.log('Creating MercadoPago preference with data:', JSON.stringify(preferenceData, null, 2));

    const response = await axios.post(
      `${MERCADOPAGO_CONFIG.BASE_URL}/checkout/preferences`,
      preferenceData,
      {
        headers: {
          'Authorization': `Bearer ${MERCADOPAGO_CONFIG.ACCESS_TOKEN}`,
          'Content-Type': 'application/json',
        },
      }
    );

    console.log('MercadoPago response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating MercadoPago preference:', error);
    throw new Error('Failed to create payment preference');
  }
};

// Función para procesar notificación de pago (webhook)
export const processPaymentNotification = async (data: any) => {
  try {
    // Verificar el estado del pago
    const paymentId = data.data?.id;
    if (!paymentId) {
      throw new Error('Payment ID not found');
    }

    // Obtener información del pago
    const response = await axios.get(
      `${MERCADOPAGO_CONFIG.BASE_URL}/v1/payments/${paymentId}`,
      {
        headers: {
          'Authorization': `Bearer ${MERCADOPAGO_CONFIG.ACCESS_TOKEN}`,
        },
      }
    );

    const payment = response.data;
    
    return {
      id: payment.id,
      status: payment.status,
      status_detail: payment.status_detail,
      external_reference: payment.external_reference,
      amount: payment.transaction_amount,
      currency: payment.currency_id,
      payment_method: payment.payment_method?.type,
      created_at: payment.date_created,
    };
  } catch (error) {
    console.error('Error processing payment notification:', error);
    throw new Error('Failed to process payment notification');
  }
};

// Función para obtener información de un pago
export const getPaymentInfo = async (paymentId: string) => {
  try {
    const response = await axios.get(
      `${MERCADOPAGO_CONFIG.BASE_URL}/v1/payments/${paymentId}`,
      {
        headers: {
          'Authorization': `Bearer ${MERCADOPAGO_CONFIG.ACCESS_TOKEN}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error getting payment info:', error);
    throw new Error('Failed to get payment information');
  }
};

// Función para redirigir al checkout de MercadoPago
export const redirectToCheckout = (preference: MercadoPagoPreference) => {
  // En desarrollo usar sandbox, en producción usar init_point
  const checkoutUrl = process.env.NODE_ENV === 'production' 
    ? preference.init_point 
    : preference.sandbox_init_point;
  
  window.location.href = checkoutUrl;
}; 